import html2pdf from 'html2pdf.js';

export interface InvoiceV2State {
  // Header
  logo: string; // base64
  companyName: string;
  companyAddress: string;
  companyPhone?: string;
  companyEmail?: string;
  companyWebsite?: string;

  // Recipient
  recipientName: string;
  recipientAddress: string;
  recipientPhone: string;
  recipientEmail?: string;

  // Details
  invoiceNumber: string;
  invoiceDate: Date | null;
  dueDate: Date | null;

  // Line Items
  items: Array<{
    id: string;
    description: string;
    quantity: number;
    rate: number;
    jobContactEntryId?: number; // Link back to timeline item for billing tracking
  }>;

  // Financials
  taxRate: number; // as decimal (0.10 for 10%)
  discountAmount: number;
  discountType: 'None' | 'Percentage' | 'Fixed';
  discountPercentage?: number;
  discountDescription?: string;
  amountPaid: number;

  // Additional
  paymentInfo: string;
  terms: string;
  notes: string;
}

// Generate PDF from invoice data by creating a temporary HTML element
export async function generateInvoicePDFFromData(invoiceData: InvoiceV2State, invoiceNumber: string): Promise<void> {
  // Generate the invoice HTML content
  const invoiceHtml = generateInvoiceHTML(invoiceData);
  
  // Create a temporary div that's visible but off-screen
  const tempDiv = document.createElement('div');
  tempDiv.style.position = 'fixed';
  tempDiv.style.left = '-2000px';
  tempDiv.style.top = '0px';
  tempDiv.style.width = '8.5in';
  tempDiv.style.height = '11in';
  tempDiv.style.backgroundColor = 'white';
  tempDiv.style.padding = '20px';
  tempDiv.style.fontFamily = 'Arial, sans-serif';
  tempDiv.style.zIndex = '-1';
  tempDiv.style.overflow = 'visible';
  
  tempDiv.innerHTML = invoiceHtml;
  
  // Add to DOM temporarily
  document.body.appendChild(tempDiv);
  
  try {
    // Wait for images and content to load
    await new Promise(resolve => setTimeout(resolve, 500));
    
    // Force a reflow to ensure all styles are applied
    const _ = tempDiv.offsetHeight; // Force reflow
    
    // Use html2pdf with optimized options for dynamic content
    const opt = {
      margin: 0.5,
      filename: `invoice-${invoiceNumber}.pdf`,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { 
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        width: tempDiv.scrollWidth,
        height: tempDiv.scrollHeight,
        scrollX: 0,
        scrollY: 0,
        windowWidth: tempDiv.scrollWidth,
        windowHeight: tempDiv.scrollHeight
      },
      jsPDF: { 
        unit: 'in', 
        format: 'letter', 
        orientation: 'portrait' 
      }
    };
    
    // Clone the element with clean positioning for better html2canvas compatibility
    const clonedDiv = tempDiv.cloneNode(true) as HTMLElement;
    clonedDiv.style.position = 'static';
    clonedDiv.style.left = 'auto';
    clonedDiv.style.top = 'auto';
    clonedDiv.style.zIndex = 'auto';
    
    await html2pdf().set(opt).from(clonedDiv).save();
    
  } finally {
    // Clean up - remove the temporary div
    document.body.removeChild(tempDiv);
  }
}

// Generate HTML content for the invoice
export function generateInvoiceHTML(data: InvoiceV2State): string {
  const formatCurrency = (amount: number) => `$${amount.toFixed(2)}`;
  const formatDate = (date: Date | null) => date ? date.toLocaleDateString() : '';
  const formatPhone = (phone: string | null) => {
    if (!phone) return '';
    // Remove all non-digits
    const digits = phone.replace(/\D/g, '');
    // Format as (XXX) XXX-XXXX for 10 digits, or XXX-XXX-XXXX for 7 digits
    if (digits.length === 10) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    } else if (digits.length === 7) {
      return `${digits.slice(0, 3)}-${digits.slice(3)}`;
    }
    return phone; // Return original if not standard format
  };
  
  return `
    <div style="max-width: 8.5in; margin: 0 auto; background: white; padding: 20px;">
      <!-- Header -->
      <div style="display: flex; justify-content: space-between; margin-bottom: 30px;">
        <div>
          ${data.logo ? `<img src="${data.logo}" alt="Company Logo" style="width: 80px; height: 80px; object-fit: contain; margin-bottom: 10px;" />` : ''}
          <h1 style="font-size: 24px; font-weight: bold; margin: 0; color: #333;">${data.companyName}</h1>
          <div style="font-size: 12px; color: #666; margin-top: 5px;">
            ${data.companyAddress.split('\n').map(line => `<div>${line}</div>`).join('')}
            ${data.companyPhone ? `<div>${formatPhone(data.companyPhone)}</div>` : ''}
            ${data.companyEmail ? `<div>${data.companyEmail}</div>` : ''}
          </div>
          
          <!-- Bill To section under company info -->
          <div style="margin-top: 20px;">
            <h3 style="font-size: 14px; font-weight: bold; margin: 0 0 10px 0; color: #333;">BILL TO:</h3>
            <div style="font-size: 12px; color: #666;">
              <div style="font-weight: bold; color: #333;">${data.recipientName}</div>
              ${data.recipientAddress.split('\n').map(line => `<div>${line}</div>`).join('')}
              ${data.recipientPhone ? `<div>${formatPhone(data.recipientPhone)}</div>` : ''}
            </div>
          </div>
        </div>
        <div style="text-align: right;">
          <h2 style="font-size: 20px; margin: 0; color: #333;">INVOICE</h2>
          <div style="font-size: 12px; color: #666; margin-top: 10px;">
            <div><strong>Invoice #:</strong> ${data.invoiceNumber}</div>
            <div><strong>Date:</strong> ${formatDate(data.invoiceDate)}</div>
            <div><strong>Due:</strong> ${formatDate(data.dueDate)}</div>
          </div>
        </div>
      </div>

      <!-- Items Table -->
      <table style="width: 100%; border-collapse: collapse; margin-bottom: 20px;">
        <thead>
          <tr style="background-color: #f5f5f5;">
            <th style="border: 1px solid #ddd; padding: 10px; text-align: left; font-size: 12px; font-weight: bold;">Description</th>
            <th style="border: 1px solid #ddd; padding: 10px; text-align: center; font-size: 12px; font-weight: bold;">Qty</th>
            <th style="border: 1px solid #ddd; padding: 10px; text-align: right; font-size: 12px; font-weight: bold;">Rate</th>
            <th style="border: 1px solid #ddd; padding: 10px; text-align: right; font-size: 12px; font-weight: bold;">Amount</th>
          </tr>
        </thead>
        <tbody>
          ${data.items.map(item => `
            <tr>
              <td style="border: 1px solid #ddd; padding: 10px; font-size: 12px;">${item.description}</td>
              <td style="border: 1px solid #ddd; padding: 10px; text-align: center; font-size: 12px;">${item.quantity}</td>
              <td style="border: 1px solid #ddd; padding: 10px; text-align: right; font-size: 12px;">${formatCurrency(item.rate)}</td>
              <td style="border: 1px solid #ddd; padding: 10px; text-align: right; font-size: 12px;">${formatCurrency(item.quantity * item.rate)}</td>
            </tr>
          `).join('')}
        </tbody>
      </table>

      <!-- Totals -->
      <div style="display: flex; justify-content: flex-end; margin-bottom: 30px;">
        <div style="width: 250px;">
          <div style="display: flex; justify-content: space-between; padding: 5px 0; font-size: 12px;">
            <span>Subtotal:</span>
            <span>${formatCurrency(data.items.reduce((sum, item) => sum + (item.quantity * item.rate), 0))}</span>
          </div>
          ${data.discountAmount > 0 ? `
            <div style="display: flex; justify-content: space-between; padding: 5px 0; font-size: 12px; color: #dc2626;">
              <span>Discount${data.discountType === 'Percentage' && data.discountPercentage ? ` (${data.discountPercentage}%)` : ''}${data.discountDescription ? ` ${data.discountDescription}` : ''}:</span>
              <span>-${formatCurrency(data.discountAmount)}</span>
            </div>
          ` : ''}
          <div style="display: flex; justify-content: space-between; padding: 5px 0; font-size: 12px;">
            <span>Tax (${(Math.round(data.taxRate * 10000) / 100).toFixed(2)}%):</span>
            <span>${formatCurrency((data.items.reduce((sum, item) => sum + (item.quantity * item.rate), 0) - data.discountAmount) * data.taxRate)}</span>
          </div>
          <div style="display: flex; justify-content: space-between; padding: 10px 0; font-size: 14px; font-weight: bold; border-top: 2px solid #333;">
            <span>Total:</span>
            <span>${formatCurrency((data.items.reduce((sum, item) => sum + (item.quantity * item.rate), 0) - data.discountAmount) * (1 + data.taxRate))}</span>
          </div>
        </div>
      </div>

      <!-- Payment Info -->
      ${data.paymentInfo ? `
        <div style="margin-bottom: 20px;">
          <h3 style="font-size: 14px; font-weight: bold; margin: 0 0 10px 0; color: #333;">PAYMENT INFORMATION:</h3>
          <div style="font-size: 12px; color: #666;">${data.paymentInfo}</div>
        </div>
      ` : ''}

      <!-- Terms -->
      ${data.terms ? `
        <div style="margin-bottom: 20px;">
          <h3 style="font-size: 14px; font-weight: bold; margin: 0 0 10px 0; color: #333;">TERMS & CONDITIONS:</h3>
          <div style="font-size: 12px; color: #666;">${data.terms}</div>
        </div>
      ` : ''}

      <!-- Notes -->
      ${data.notes ? `
        <div>
          <h3 style="font-size: 14px; font-weight: bold; margin: 0 0 10px 0; color: #333;">NOTES:</h3>
          <div style="font-size: 12px; color: #666;">${data.notes}</div>
        </div>
      ` : ''}
    </div>
  `;
}

export async function generateInvoicePDF(invoiceRef: HTMLElement, invoiceNumber: string): Promise<void> {
  // Clone the element and remove interactive elements
  const clonedElement = invoiceRef.cloneNode(true) as HTMLElement;
  
  // Remove all interactive elements
  const interactiveElements = clonedElement.querySelectorAll('input[type="file"], button, [class*="cursor-pointer"], [class*="hover:"], [class*="group"]');
  interactiveElements.forEach(el => {
    if (el.tagName === 'INPUT' && (el as HTMLInputElement).type === 'file') {
      el.remove(); // Remove file inputs completely
    } else if (el.tagName === 'BUTTON') {
      el.remove(); // Remove all buttons
    } else {
      // Remove interactive classes
      el.classList.remove('cursor-pointer', 'hover:bg-gray-50', 'hover:border-blue-500', 'hover:opacity-80', 'group');
    }
  });

  // Remove any remaining file input elements
  const fileInputs = clonedElement.querySelectorAll('input[type="file"]');
  fileInputs.forEach(input => input.remove());

  // Remove any remaining buttons
  const buttons = clonedElement.querySelectorAll('button');
  buttons.forEach(button => button.remove());

  // Ensure logo images have proper inline styles for PDF
  const logoImages = clonedElement.querySelectorAll('img[alt="Company Logo"]');
  logoImages.forEach(img => {
    // Logo should already be base64 from the component
    img.style.width = '80px';
    img.style.height = '48px';
    img.style.objectFit = 'contain';
    img.style.display = 'block';
    img.style.maxWidth = '80px';
    img.style.maxHeight = '48px';
  });

  // Ensure proper layout for PDF
  clonedElement.style.fontFamily = 'Arial, sans-serif';
  clonedElement.style.lineHeight = '1.4';
  clonedElement.style.maxWidth = '8.5in';
  clonedElement.style.margin = '0 auto';
  clonedElement.style.padding = '48px';

  const opt = {
    margin: [0.5, 0.5, 0.5, 0.5],
    filename: `invoice-${invoiceNumber}.pdf`,
    image: { type: 'jpeg', quality: 0.98 },
    html2canvas: { 
      scale: 2,
      useCORS: true,
      allowTaint: true,
      logging: false,
      letterRendering: true
    },
    jsPDF: { 
      unit: 'in', 
      format: 'letter', 
      orientation: 'portrait',
      compress: true
    },
    pagebreak: { mode: ['avoid-all', 'css', 'legacy'] }
  };

  try {
    await html2pdf().set(opt).from(clonedElement).save();
  } catch (error) {
    console.error('Error generating PDF:', error);
    throw new Error('Failed to generate PDF');
  }
}

export function printInvoice(invoiceRef: HTMLElement): void {
  const printWindow = window.open('', '_blank');
  if (!printWindow) {
    throw new Error('Unable to open print window');
  }

  // Clone the invoice element
  const clonedElement = invoiceRef.cloneNode(true) as HTMLElement;
  
  // Remove all interactive elements
  const interactiveElements = clonedElement.querySelectorAll('input[type="file"], button, [class*="cursor-pointer"], [class*="hover:"], [class*="group"]');
  interactiveElements.forEach(el => {
    if (el.tagName === 'INPUT' && (el as HTMLInputElement).type === 'file') {
      el.remove(); // Remove file inputs completely
    } else if (el.tagName === 'BUTTON') {
      el.remove(); // Remove all buttons
    } else {
      // Remove interactive classes
      el.classList.remove('cursor-pointer', 'hover:bg-gray-50', 'hover:border-blue-500', 'hover:opacity-80', 'group');
    }
  });

  // Remove any remaining file input elements
  const fileInputs = clonedElement.querySelectorAll('input[type="file"]');
  fileInputs.forEach(input => input.remove());

  // Remove any remaining buttons
  const buttons = clonedElement.querySelectorAll('button');
  buttons.forEach(button => button.remove());

  // Add print styles
  const printStyles = `
    <style>
      @media print {
        body { margin: 0; }
        .no-print { display: none !important; }
        .print-break { page-break-after: always; }
      }
      body { font-family: Arial, sans-serif; }
      .invoice-container { max-width: 8.5in; margin: 0 auto; }
    </style>
  `;

  printWindow.document.write(`
    <html>
      <head>
        <title>Invoice Print</title>
        ${printStyles}
      </head>
      <body>
        <div class="invoice-container">
          ${clonedElement.outerHTML}
        </div>
      </body>
    </html>
  `);

  printWindow.document.close();
  printWindow.print();
  printWindow.close();
}

export async function generateInvoicePreviewUrl(invoiceRef: HTMLElement): Promise<string> {
  // Clone the element and remove interactive elements
  const clonedElement = invoiceRef.cloneNode(true) as HTMLElement;
  
  // Remove all interactive elements
  const interactiveElements = clonedElement.querySelectorAll('input[type="file"], button, [class*="cursor-pointer"], [class*="hover:"], [class*="group"]');
  interactiveElements.forEach(el => {
    if (el.tagName === 'INPUT' && (el as HTMLInputElement).type === 'file') {
      el.remove(); // Remove file inputs completely
    } else if (el.tagName === 'BUTTON') {
      el.remove(); // Remove all buttons
    } else {
      // Remove interactive classes
      el.classList.remove('cursor-pointer', 'hover:bg-gray-50', 'hover:border-blue-500', 'hover:opacity-80', 'group');
    }
  });

  // Remove any remaining file input elements
  const fileInputs = clonedElement.querySelectorAll('input[type="file"]');
  fileInputs.forEach(input => input.remove());

  // Remove any remaining buttons
  const buttons = clonedElement.querySelectorAll('button');
  buttons.forEach(button => button.remove());

  // Ensure logo images have proper inline styles for PDF
  const logoImages = clonedElement.querySelectorAll('img[alt="Company Logo"]');
  logoImages.forEach(img => {
    // Logo should already be base64 from the component
    img.style.width = '80px';
    img.style.height = '48px';
    img.style.objectFit = 'contain';
    img.style.display = 'block';
    img.style.maxWidth = '80px';
    img.style.maxHeight = '48px';
  });

  // Ensure proper layout for PDF
  clonedElement.style.fontFamily = 'Arial, sans-serif';
  clonedElement.style.lineHeight = '1.4';
  clonedElement.style.maxWidth = '8.5in';
  clonedElement.style.margin = '0 auto';
  clonedElement.style.padding = '48px';

  // Generate PDF blob for preview
  const opt = {
    margin: [0.5, 0.5, 0.5, 0.5],
    image: { type: 'jpeg', quality: 0.98 },
    html2canvas: { 
      scale: 2,
      useCORS: true,
      allowTaint: true,
      logging: false,
      letterRendering: true
    },
    jsPDF: { 
      unit: 'in', 
      format: 'letter', 
      orientation: 'portrait',
      compress: true
    },
    pagebreak: { mode: ['avoid-all', 'css', 'legacy'] }
  };

  try {
    const pdf = await html2pdf().set(opt).from(clonedElement).outputPdf('blob');
    return URL.createObjectURL(pdf);
  } catch (error) {
    console.error('Error generating PDF preview:', error);
    throw new Error('Failed to generate PDF preview');
  }
}
